import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Button,
  Box,
  Dropdown,
  Text,
  TextField,
  Textarea,
  Badge,
  Spread,
} from 'braid-design-system';
import { useState, useEffect } from 'react';
// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';

import { useAuth } from 'src/shared/auth/AuthProvider';
import { useConfig } from 'src/shared/hooks';
import { isSeekTestEmail, siteNameByBrand } from 'src/utils/helpers';
import { trackEvent } from 'src/utils/trackEvent';

import translations from '../../../../.vocab';
import {
  getSuggestionByCountry,
  type SuggestionByCountry,
} from '../../helpers/suggestionMapping';
import ArticleSuggest from '../ArticleSuggest/ArticleSuggest';
import { ContactNumber } from '../ContactNumber/ContactNumber';
import translations_subject from '../SubjectData/.vocab';
import { useSubjects } from '../SubjectData/useSubjects';
import { TermsPrivacy } from '../TermsPrivacy/TermsPrivacy';

import { useFields } from './useFields';

import * as styles from './Form.css';

const RECAPTCHA_KEY = '6Ldud48UAAAAAJnZslIKFnHXseMW2GdNFXcT9wzg';
interface FormProps {
  handleSubmit: any;
  setFieldValue: any;
  setSubmitted: (submitted: boolean) => void;
  resetForm: any;
}

const Form = ({
  handleSubmit,
  setFieldValue,
  setSubmitted,
  resetForm,
}: FormProps) => {
  const [invalidResponse, setInvalidResponse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // const [currentSubject, setSubject] = useState<string | undefined>(undefined);
  const [reasonList, setReasonList] = useState<string[]>([]);
  const [articleSuggestion, setArticleSuggestion] = useState<
    SuggestionByCountry | undefined
  >(undefined);
  const [recaptcha, setRecaptcha] = useState('');
  // const lastLoggedSuggestion = useRef('');
  const auth: any = useAuth();
  const { authenticationStatus } = auth;
  const { brand, site, language, country, environment } = useConfig();
  const { t } = useTranslations(translations);
  const { t: t_subject } = useTranslations(translations_subject); // TO-DO: use this to clean up the translations

  const isAnz = site.includes('candidate-seek');

  const { subjects } = useSubjects(brand);

  const postUrl =
    environment === 'production'
      ? 'https://wpt-contact-api.cloud.seek.com.au/candidate'
      : 'https://wpt-contact-api.staging.cloud.seek.com.au/candidate';

  const onSubmit = handleSubmit((formValues: any) => {
    if (recaptcha) {
      setSubmitting(true);
      let candidateId = '';
      let tags;
      if (authenticationStatus === 'authenticated') {
        candidateId = auth.id;
        tags = auth.tags;
      } else if (isSeekTestEmail(email.value)) {
        tags = {
          testRecord: true,
        };
      }
      const userAgent = navigator.userAgent.toString();
      let { additionalInformation, ...otherValues } = formValues; // eslint-disable-line prefer-const
      additionalInformation = `${additionalInformation}\n\nSite: ${site}\nLanguage: ${language}\nWebForm\nUserAgent: ${userAgent}`;
      const data = {
        candidateId,
        additionalInformation,
        ...otherValues,
        siteCountry: country.toUpperCase(),
        recaptcha,
        tags,
      };
      trackEvent(
        'content_element_pressed',
        {
          elementText: 'submit',
        },
        'link',
      );
      fetch(postUrl, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then((response) => {
          if (response.status === 200) {
            setSubmitted(true);
            setSubmitting(false);
            setInvalidResponse(false);
            resetForm();
          }
        })
        .catch(() => {
          setSubmitting(false);
          setInvalidResponse(true);
        });
    }
  });

  const { subject, reason, firstName, lastName, email, additionalInformation } =
    useFields();

  const subjectList = Object.keys(subjects || {});

  useEffect(() => {
    if (authenticationStatus === 'authenticated') {
      setFieldValue('firstName', auth.firstName);
      setFieldValue('lastName', auth.lastName);
      setFieldValue('email', auth.emailAddress);
    }
  }, [authenticationStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubjectChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const subjectValue = event.currentTarget.value;
    setFieldValue('subject', subjectValue);
    setFieldValue('reason', '');
    // setSubject(subjectValue)
    // Just use the subjects data directly - we'll handle suggestions in reasonChange
    setReasonList(subjects[subjectValue] || []);
  };

  const handleReasonChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const reasonValue = event.currentTarget.value;

    setFieldValue('reason', reasonValue);

    if (!reasonValue || !subject) {
      setArticleSuggestion(undefined);
      return;
    }

    const suggestion = getSuggestionByCountry(
      subject.value,
      reasonValue,
      country,
    );

    setArticleSuggestion(suggestion);
  };

  return (
    <form onSubmit={onSubmit}>
      <Spread space="large" direction="vertical">
        <TextField
          id="firstName"
          label={t('First name')}
          message={!firstName.valid && firstName.errorMessage}
          onClear={() => setFieldValue('firstName', '')}
          tone={!firstName.valid ? 'critical' : 'neutral'}
          {...firstName}
        />
        <TextField
          id="lastName"
          label={t('Last name')}
          message={!lastName.valid && lastName.errorMessage}
          onClear={() => setFieldValue('lastName', '')}
          tone={!lastName.valid ? 'critical' : 'neutral'}
          {...lastName}
        />
        {!isAnz ? (
          <ContactNumber id="telephone" label={t('Contact number')} />
        ) : null}
        <TextField
          id="email"
          label={t('Email')}
          onClear={() => setFieldValue('email', '')}
          message={!email.valid && email.errorMessage}
          tone={!email.valid ? 'critical' : 'neutral'}
          {...email}
        />
        <Dropdown
          id="subject"
          label={t('Subject')}
          placeholder={t('Please select')}
          message={subject.errorMessage}
          tone={subject.errorMessage ? 'critical' : 'neutral'}
          {...subject}
          onChange={handleSubjectChange}
        >
          {subjectList.map((item) => {
            const resolvedItem = item.replace(
              '{brandName}',
              siteNameByBrand[brand],
            );
            return (
              <option key={resolvedItem} value={resolvedItem}>
                {/* {item} */}
                {t_subject(item as any, { brandName: siteNameByBrand[brand] })}
              </option>
            );
          })}
        </Dropdown>
        {reasonList.length > 0 && (
          <Dropdown
            id="reason"
            label={t('Reason')}
            placeholder={t('Please select')}
            message={reason.errorMessage}
            tone={reason.errorMessage ? 'critical' : 'neutral'}
            {...reason}
            onChange={handleReasonChange}
          >
            {reasonList.map((item) => (
              <option key={item} value={item}>
                {t_subject(item as any, { brandName: siteNameByBrand[brand] })}
              </option>
            ))}
          </Dropdown>
        )}
        {articleSuggestion && <ArticleSuggest suggestion={articleSuggestion} />}
        <Textarea
          id="additionalInformation"
          label={t('Additional Information')}
          characterLimit={1000}
          secondaryLabel={t('Max 1000 characters')}
          value={additionalInformation.value}
          onChange={additionalInformation.onChange}
          onBlur={additionalInformation.onBlur}
          message={
            !additionalInformation.valid && additionalInformation.errorMessage
          }
          tone={!additionalInformation.valid ? 'critical' : 'neutral'}
        />
        <TermsPrivacy />
        <Box className={styles.captchaContainer}>
          <ReCAPTCHA
            sitekey={RECAPTCHA_KEY}
            onChange={(val: any) => setRecaptcha(val)}
            hl={language.slice(0, 2)}
          />
        </Box>
        {invalidResponse && (
          <Alert tone="critical">
            <Text id="api-failure-text">
              {t(
                'There was a problem submitting your request. Please try again.',
              )}
            </Text>
          </Alert>
        )}
        <Actions>
          <Button tone="formAccent" type="submit" loading={submitting}>
            {submitting ? t('Sending') : t('Send')}
          </Button>
        </Actions>
        {environment !== 'production' && (
          <Box onClick={prefillForm(setFieldValue)} id="prefill-form">
            <Badge>Prefill form (testing only)</Badge>
          </Box>
        )}
      </Spread>
    </form>
  );
};

export default Form;

const prefillForm = (setFieldValue: FormProps['setFieldValue']) => () => {
  setFieldValue('firstName', 'TestFirstName');
  setFieldValue('lastName', 'TestLastName');
  setFieldValue('email', 'testfirstname+test-testlastname@seekasia.com');
  setFieldValue('company', 'TestCompany');
  setFieldValue('subject', 'Job Search');
  setFieldValue('reason', 'General feedback');
  setFieldValue('additionalInformation', 'Test only, please ignore');
};
